import { useState, useEffect, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { UnitDTO } from "shared/types/unit";

export const useVacantUnits = () => {
  const { session } = useSession();
  const [vacantUnits, setVacantUnits] = useState<UnitDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVacantUnits = useCallback(async () => {
    try {
      setIsLoading(true);
      // Use the enhanced endpoint so we get applicationStats (candidate count)
      // along with broker/visitation data needed for the leegstand columns.
      const response = await fetchWithAuth(
        `${API_URL}/rental/metrics/enhanced-vacant-units`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      setVacantUnits(data);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch vacant units:", error);
      setError("Failed to load vacant units");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchVacantUnits();
    }
  }, [session, fetchVacantUnits]);

  return { vacantUnits, isLoading, error, refetch: fetchVacantUnits };
};

export const useUpcomingVacantUnits = () => {
  const { session } = useSession();
  const [upcomingVacantUnits, setUpcomingVacantUnits] = useState<UnitDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUpcomingVacantUnits = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/metrics/enhanced-upcoming-vacant-units`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      setUpcomingVacantUnits(data);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch upcoming vacant units:", error);
      setError("Failed to load upcoming vacant units");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchUpcomingVacantUnits();
    }
  }, [session, fetchUpcomingVacantUnits]);

  return { upcomingVacantUnits, isLoading, error, refetch: fetchUpcomingVacantUnits };
};

export const useUnitsInRenovation = () => {
  const { session } = useSession();
  const [unitsInRenovation, setUnitsInRenovation] = useState<UnitDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUnitsInRenovation = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/units/in-renovation`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      setUnitsInRenovation(data);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch units in renovation:", error);
      setError("Failed to load units in renovation");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchUnitsInRenovation();
    }
  }, [session, fetchUnitsInRenovation]);

  return { unitsInRenovation, isLoading, error, refetch: fetchUnitsInRenovation };
};

export const useAlmostRentedUnits = () => {
  const { session } = useSession();
  const [almostRentedUnits, setAlmostRentedUnits] = useState<UnitDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlmostRentedUnits = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/units/almost-rented`,
        { method: "GET" },
        session
      );
      if (response.ok) {
        const data = await response.json();
        setAlmostRentedUnits(data);
        setError(null);
      }
    } catch (error) {
      console.error("Failed to fetch almost rented units:", error);
      setError("Failed to load almost rented units");
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchAlmostRentedUnits();
    }
  }, [session, fetchAlmostRentedUnits]);

  return { almostRentedUnits, isLoading, error, refetch: fetchAlmostRentedUnits };
};
